"use client";
import React, { useEffect, useRef, useState } from "react";
import ChatInterface from "@/components/ChatComponent/ChatScreen/ChatInterface/ChatInterface";
import ChatInbox from "@/components/ChatComponent/ChatInbox/ChatInbox";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import chatService from "@/service/chat.service";
import { Box, Typography, Grid, Paper, Divider } from "@mui/material";
import { ROLES } from "@/constants/data.const";
import ChatMessageShimmer from "../Skeleton/ChatMessageShimmer";
import InboxShimmer from "../Skeleton/InboxShimmer";

function ChatContainer() {
  const [receiverId, setReceiverId] = useState(null);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const chatSectionRef = useRef(null);

  const location = useLocation();
  const { vendorId = null, customerId = null } = location.state || {};
  const auth = useSelector((state) => state.auth.auth);
  const twoFactorEnabled = auth.two_factor_enabled || false;

  useEffect(() => {
    const fetchConversationDetails = async () => {
      setLoading(true);
      try {
        // 2FA status is now included in auth object from backend
        const response = await chatService.fetchChatHistory(auth._id, auth.role);
        const allConversations = response?.data?.conversations || [];
        setConversations(allConversations);

        if (allConversations.length > 0) {
          const selectedConversation = allConversations.find((conv) =>
            auth.role === ROLES.CUSTOMER ? conv.vendor._id === vendorId : conv.customer._id === customerId
          );

          if (selectedConversation) {
            setSelectedConversationId(selectedConversation._id);
            setReceiverId(auth.role === ROLES.CUSTOMER ? vendorId : customerId);
            setUser(auth.role === ROLES.CUSTOMER ? selectedConversation.vendor : selectedConversation.customer);
          } else {
            const first = allConversations[0];
            setSelectedConversationId(first._id);
            setReceiverId(auth.role === ROLES.CUSTOMER ? first.vendor._id : first.customer._id);
            setUser(auth.role === ROLES.CUSTOMER ? first.vendor : first.customer);
          }
        }
        if (window.innerWidth <= 768) {
          setTimeout(() => {
            chatSectionRef.current?.scrollIntoView({ behavior: "smooth" });
          }, 100); // give a little time for DOM to update
        }
      } catch (error) {
        setError("Failed to fetch chat data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    if (auth._id && auth.role) {
      fetchConversationDetails();
    }
  }, [vendorId, customerId, auth]);

  const onSelectConversation = (conversation) => {
    setSelectedConversationId(conversation._id);
    setReceiverId(auth.role === ROLES.CUSTOMER ? conversation.vendor._id : conversation.customer._id);
    const userObj = auth.role === ROLES.CUSTOMER ? conversation.vendor : conversation.customer;
    setUser(userObj);
      // Scroll to chat on small screens
  if (window.innerWidth <= 768) {
    setTimeout(() => {
      chatSectionRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100); // give a little time for DOM to update
  }
  };

  const filteredConversations = conversations.filter((conv) => {
    const userName =
      auth.role === ROLES.CUSTOMER
        ? `${conv.vendor?.first_name} ${conv.vendor?.last_name}`
        : `${conv.customer?.first_name} ${conv.customer?.last_name}`;
    return userName.toLowerCase().includes(searchQuery.toLowerCase());
  });

  return (
    <Box sx={{ mt: '10px', pl:'5px' }}>
      {/* Header with Inbox and Encryption Notice on same line */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Typography
          variant="h4"
          fontWeight="bold"
          sx={{ color: "#5e5acf" }}
        >
          Inbox
        </Typography>
      </Box>

      <Box
        elevation={1}
        height="100%"
      >
        <Grid container spacing={1}>
            <Grid
              item
              md={4}
              xs={10}
              maxWidth={'100%'}
              flexBasis={'100%'}
              sx={{
                p: { xs: 1, sm: 0 },
                height: { xs: 'calc(100vh - 100px)', md: 'calc(100vh - 120px)' }, // adjust based on header
                overflowY: { xs: 'auto', md: 'hidden' }, // Remove scroll on desktop only
              }}
            >
            {loading ? (
              <Box
                // sx={{
                //   width: '100%',
                //   height: 'calc(100vh - 150px)',
                //   overflowY: 'auto',
                // }}
              >
                <InboxShimmer />
              </Box>
            ) : (
              <ChatInbox
                onSelectConversation={onSelectConversation}
                conversations={filteredConversations}
                userType={auth.role}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                loading={loading}
                error={error}
              />
            )}
          </Grid>

            <Grid 
              item 
              xs={12} 
              md={8} 
              sx={{ 
                p: { xs: 1, sm: 1 },
                height: { xs: 'calc(100vh - 150px)', md: 'calc(100vh - 120px)' },
                overflowY: { xs: 'auto', md: 'hidden' } // Remove scroll on desktop only
              }}  
              ref={chatSectionRef}
            >
              {loading ? (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%', // Use full height instead of calc
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    p: 2,
                  }}
                >
                <ChatMessageShimmer />
              </Box>
              ) : !selectedConversationId ? (
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mt: 4,
                    mx: 3,
                    p: 3,
                    backgroundColor: '#f5f5f5',
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    textAlign: 'center',
                    outline: 'none',
                    '&:focus': {
                      outline: 'none',
                    },
                  }}
                >
                <Typography variant="body1" color="text.secondary">
                  No conversations selected. Please select a conversation to start chatting.
                </Typography>
              </Box>
            ) : (
              <ChatInterface
                conversationId={selectedConversationId}
                role={auth.role}
                receiverId={receiverId}
                chatUser={user}
                twoFactorEnabled={twoFactorEnabled}
                userRole={auth.role}
              />
            )}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

export default ChatContainer;
